import React, { forwardRef, useState } from "react";
import DatePicker from "react-datepicker";
import {
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
} from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";

import "react-datepicker/dist/react-datepicker.css";
import { useRecordContext } from "react-admin";
import moment from "moment";

interface ICustomDatePickerOutsideFormInput {
  name: string;
  label?: string;
  min?: string;
  max?: string;
  placeholderValue?: string;
  disableValue?: boolean;
  excludeDates?: IExcludeDates[];
  includeDates?: IIncludeDates[];
  isWeekDays?: boolean;
  parentCallback?: any;
}

const useStyles = makeStyles({
  root: {
    marginTop: "8px",
    paddingBottom: "3px",
    width: "100%",
  },
  override: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      height: "50px",
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
  picker: {
    "& .react-datepicker-wrapper": {
      width: "100%",
    },
    "& .react-datepicker-popper": {
      zIndex: 10,
    },
  },
});

const CustomDatePickerOutsideFormInput = ({
  name,
  label,
  min,
  max,
  excludeDates,
  includeDates,
  disableValue,
  placeholderValue,
  isWeekDays,
  parentCallback,
}: ICustomDatePickerOutsideFormInput) => {
  const classes = useStyles();
  const record = useRecordContext();

  const [date, setDate] = useState(
    record[name] !== undefined ? new Date(record[name]) : ""
  );

  const getDateFormatted = (fullDate: Date) => {
    const date = ("0" + fullDate.getDate()).slice(-2);
    const month = ("0" + (fullDate.getMonth() + 1)).slice(-2);
    const year = fullDate.getFullYear();

    return `${year}-${month}-${date}`;
  };

  const isWeekday = (date: Date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };

  const SetExcludeDates = (excludeDates: IExcludeDates[]) => {
    const listExcludeDates: any = [];

    if (!excludeDates) {
      return;
    }

    if (excludeDates.length < 0) {
      return;
    }

    excludeDates.forEach((excludeDate: IExcludeDates) => {
      let start;

      if (excludeDate.hasOwnProperty("endDate")) {
        if (
          excludeDate.startDate.slice(0, 10) >=
          new Date().toISOString().slice(0, 10)
        ) {
          start = excludeDate.startDate;
        } else {
          start = getDateFormatted(new Date());
        }

        const startDate = start;
        const endDate = excludeDate.endDate.slice(0, 10);
        const dateMove = new Date(startDate);
        let strDate = startDate;

        while (strDate < endDate) {
          strDate = getDateFormatted(dateMove);
          listExcludeDates.push(new Date(strDate));
          dateMove.setDate(dateMove.getDate() + 1);
        }
      } else {
        listExcludeDates.push(new Date(excludeDate.startDate));
      }
    });

    return listExcludeDates;
  };

  const SetIncludeDates = (includeDates: IIncludeDates[]) => {
    const listIncludeDates: any = [];

    if (!includeDates) {
      return;
    }

    if (includeDates.length < 0) {
      return;
    }

    includeDates.forEach((includeDates: IIncludeDates) => {
      let start;

      if (includeDates.hasOwnProperty("endDate")) {
        if (
          includeDates.startDate.slice(0, 10) >=
          new Date().toISOString().slice(0, 10)
        ) {
          start = includeDates.startDate;
        } else {
          start = getDateFormatted(new Date());
        }

        const startDate = start;
        const endDate = includeDates.endDate.slice(0, 10);
        const dateMove = new Date(startDate);
        let strDate = startDate;

        while (strDate < endDate) {
          strDate = getDateFormatted(dateMove);
          listIncludeDates.push(new Date(strDate));
          dateMove.setDate(dateMove.getDate() + 1);
        }
      } else {
        listIncludeDates.push(new Date(listIncludeDates.startDate));
      }
    });

    return listIncludeDates;
  };

  const CustomInput = forwardRef(({ onClick }: any, ref: any) => {
    return (
      <div className={classes.override}>
        <TextField
          className={classes.root}
          variant="outlined"
          label={label}
          defaultValue={
            placeholderValue
              ? placeholderValue
              : date !== ""
              ? moment(date).format("DD/MM/yyyy")
              : ""
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end">
                  <CalendarTodayIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onClick={onClick}
          ref={ref}
          disabled={disableValue}
        />
      </div>
    );
  });

  return (
    <div className={classes.picker}>
      <DatePicker
        dateFormat="dd/MM/yyyy"
        selected={date}
        minDate={min ? new Date(min) : ""}
        maxDate={max ? new Date(max) : ""}
        excludeDates={SetExcludeDates(excludeDates)}
        includeDates={SetIncludeDates(includeDates)}
        showPopperArrow={false}
        onChange={(fullDate: any) => {
          setDate(fullDate);
          parentCallback && parentCallback(getDateFormatted(fullDate));
        }}
        filterDate={isWeekDays ? isWeekday : ""}
        peekNextMonth
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        customInput={<CustomInput />}
        disabled={disableValue}
        todayButton="Today"
      />
    </div>
  );
};

export default CustomDatePickerOutsideFormInput;
